// @flow
import React from 'react';
import {connect} from 'react-redux';

import Filters from '@homeaway/react-search-filters';
import Experiment, {On, Off} from '@homeaway/react-experiment';
import FilteredCountDataProvider from '../FilteredCountDataProvider/FilteredCountDataProvider';
import FormModal from '../FormModal/FormModal';

import {media} from '../../utils/breakpoints';

import routerActions from '../../actions/router';
import overlayActions from '../../actions/overlay';
import searchParamsActions from '../../actions/searchParams';
import {FILTERS_MODAL} from '../../actions/modals';

import type {State as ReduxState} from '../../reducers/index';
import type {Action as OverlayAction} from '../../actions/overlay';
import type {SearchParamsType} from '../../reducers/searchParams';
import type {FilterGroupType} from '@homeaway/react-search-filters';
import type {LinguaProps} from '../withLingua/withLingua';

import {
    instantBookFilter,
    getCancellationFilterId,
    getLengthOfStay
} from '../../utils/filters';
import {HA_TOTAL_PRICE_FILTER} from '../../abacus/experiments';

export type FilterDispatchProps = {
    filter: typeof routerActions.filter,
    onDone: () => void,
    resetInputParams: typeof searchParamsActions.resetInputParams,
    syncInputParams: typeof searchParamsActions.syncInputParams,
    updateFilterCheck: typeof searchParamsActions.updateFilterCheck,
    updateMultipleFilterCheck?: typeof searchParamsActions.updateMultipleFilterCheck,
    updateInputParams: typeof searchParamsActions.updateInputParams,
    updatePriceInputValuesValidity: typeof searchParamsActions.updatePriceInputValuesValidity
};

export type FiltersProps = {
    currency: string,
    hasDates: boolean,
    inputParamsAreChanged: boolean,
    inputParamsAreDefault: boolean,
    isMobile: boolean,
    lengthOfStay?: ?number,
    locale: string,
    numFiltersPanel?: ?number,
    priceInputValuesAreValid: boolean,
    viewportHeight: number
};

export type FilterFormProps = {
    filterGroups: FilterGroupType[],
    inputParams: SearchParamsType,
    modalIsOpen: boolean,
    resultsCount?: ?number
} & FiltersProps &
    FilterDispatchProps;

export type FilterBarProps = {
    allFiltersButtonOnly?: Boolean,
    allFiltersButtonCustomTrigger?: Element,
    currentBedrooms: number,
    exactBedroomsMatch: boolean,
    hideOverlay: () => OverlayAction,
    inputMaxBedrooms: number,
    inputMaxNightlyPrice: number,
    inputMaxTotalPrice: number | null,
    inputMinBedrooms: number,
    inputMinNightlyPrice: number,
    inputMinTotalPrice: number | null,
    isCancellationPoliciesFilterSelected: boolean,
    isMoreFiltersSelected: boolean,
    isOverlayVisible: boolean,
    maxBedrooms: number,
    maxNightlyPrice: number,
    maxTotalPrice: number | null,
    minBedrooms: number,
    minNightlyPrice: number,
    minTotalPrice: number | null,
    searchResultsLoading: boolean,
    showOverlay: () => OverlayAction,
    siteName: string,
    topBannersHeight: number
} & FiltersProps &
    FilterDispatchProps;

export type FilterContainerProps = FilterBarProps &
    FilterFormProps &
    FilterDispatchProps &
    LinguaProps;

export function FiltersContainer(props: FilterContainerProps) {
    return (
        <Experiment name={HA_TOTAL_PRICE_FILTER}>
            <On>
                <Filters
                    {...props}
                    filterCountDataProvider={FilteredCountDataProvider}
                    modal={FormModal}
                    priceFilterOptions={{
                        isFilterByTotalPriceEnabled: props.hasDates,
                        lengthOfStay: props.lengthOfStay,
                        showFilterByPriceType: true
                    }}
                />
            </On>
            <Off>
                <Filters
                    {...props}
                    filterCountDataProvider={FilteredCountDataProvider}
                    modal={FormModal}
                />
            </Off>
        </Experiment>
    );
}

export const mapStateToProps = (state: ReduxState) => {
    // select params that only apply to this component
    const {
        modals,
        overlay: {showOverlay: isOverlayVisible},
        searchParams: {
            inputParams,
            inputParamsAreChanged,
            inputParamsAreDefault,
            numFiltersPanel,
            params,
            params: {
                filter,
                filterByTotalPrice,
                maxBathrooms,
                maxBedrooms,
                maxNightlyPrice,
                maxTotalPrice,
                minBathrooms,
                minBedrooms,
                minNightlyPrice,
                minTotalPrice
            },
            priceInputValuesAreValid,
            selectedFilters
        },
        system: {currency, siteName, fullLocale},
        topBanners: {height: topBannersHeight},
        viewport: {height, breakpoint}
    } = state;

    const hasDates = !!(inputParams.arrival && inputParams.departure);

    return {
        bedroomsExactMatch: inputParams.minBedrooms === inputParams.maxBedrooms,
        canFit3Columns: media(breakpoint, {minWidth: 'MD_MIN'}),
        canFit4Columns: media(breakpoint, {minWidth: 'LG_MIN'}),
        currency,
        filterByTotalPrice: hasDates && filterByTotalPrice,
        hasDates,
        inputFilterByTotalPrice: hasDates && inputParams.filterByTotalPrice,
        inputMaxBathrooms: inputParams.maxBathrooms,
        inputMaxBedrooms: inputParams.maxBedrooms,
        inputMaxNightlyPrice: inputParams.maxNightlyPrice,
        inputMaxTotalPrice: inputParams.maxTotalPrice,
        inputMinBathrooms: inputParams.minBathrooms,
        inputMinBedrooms: inputParams.minBedrooms,
        inputMinNightlyPrice: inputParams.minNightlyPrice,
        inputMinTotalPrice: inputParams.minTotalPrice,
        inputParams,
        inputParamsAreChanged,
        inputParamsAreDefault,
        isMobile: !media(breakpoint, {minWidth: 'SM_MIN'}),
        isCancellationPoliciesFilterSelected: !!getCancellationFilterId(filter),
        isMoreFiltersSelected: !!(
            (minBathrooms && minBathrooms > 0) ||
            (maxBathrooms && maxBathrooms > 0) ||
            (filter && filter.length > 0 && !instantBookFilter(filter))
        ),
        isOverlayVisible,
        lengthOfStay: getLengthOfStay(
            inputParams.arrival || '',
            inputParams.departure || ''
        ),
        locale: fullLocale,
        maxBathrooms,
        maxBedrooms,
        maxNightlyPrice,
        maxTotalPrice,
        minBathrooms,
        minBedrooms,
        minNightlyPrice,
        minTotalPrice,
        modalIsOpen: modals.FILTERS.isOpen,
        numFiltersPanel,
        params,
        priceInputValuesAreValid,
        selectedFilters,
        siteName,
        topBannersHeight,
        viewportHeight: height
    };
};

export const mapDispatchToProps = {
    filter: routerActions.filter,
    hideOverlay: () => overlayActions.toggleOverlay(false),
    onDone: () => routerActions.popModal(FILTERS_MODAL),
    resetInputParams: searchParamsActions.resetInputParams,
    showOverlay: () => overlayActions.toggleOverlay(true),
    syncInputParams: searchParamsActions.syncInputParams,
    updateFilterCheck: searchParamsActions.updateFilterCheck,
    updateMultipleFilterCheck: searchParamsActions.updateMultipleFilterCheck,
    updateInputParams: searchParamsActions.updateInputParams,
    updatePriceInputValuesValidity:
        searchParamsActions.updatePriceInputValuesValidity,
    clearFilters: searchParamsActions.clearFilters
};

const ConnectedFiltersContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(FiltersContainer);

export default ConnectedFiltersContainer;
